import React, { useState } from 'react';
import { AccountProfile } from './settings/AccountProfile';

interface User {
  id: string;
  email: string | null;
  name: string;
  isAnonymous: boolean;
  permissions: string[];
  mobile_number?: string | null;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onUserUpdate?: () => void;
}

type SettingsSection = 'account-profile';

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  user,
  onUserUpdate,
}) => {
  const [activeSection, setActiveSection] = useState<SettingsSection>('account-profile');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  if (!isOpen) return null;

  const handleSectionChange = (section: SettingsSection) => {
    setActiveSection(section);
    setIsMobileNavOpen(false); // Close mobile nav when section is selected
  };

  const NavButton = ({ section, icon, label }: { section: SettingsSection; icon: React.ReactNode; label: string }) => (
    <button
      onClick={() => handleSectionChange(section)}
      className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
        activeSection === section
          ? 'bg-[#74AA9C] text-white'
          : 'text-[#080808] hover:bg-gray-100'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const navigationContent = (
    <nav className="space-y-1">
      <NavButton
        section="account-profile"
        icon={
          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        }
        label="Account Profile"
      />

      {/* Placeholder for future sections */}
      {/* <NavButton
        section="notifications"
        icon={
          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        }
        label="Notifications"
      /> */}
    </nav>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal - Full screen on mobile, centered modal on desktop */}
      <div className="relative bg-white shadow-xl w-full h-full md:rounded-lg md:max-w-4xl md:mx-4 md:max-h-[90vh] md:h-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            {/* Hamburger menu button - visible only on small screens */}
            <button
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              className="md:hidden mr-3 p-1 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Toggle navigation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileNavOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            <h2 className="text-xl font-semibold text-[#080808]">Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Mobile Navigation Overlay */}
          {isMobileNavOpen && (
            <div
              className="md:hidden absolute inset-0 bg-black bg-opacity-25 z-10"
              onClick={() => setIsMobileNavOpen(false)}
            />
          )}

          {/* Sidebar Navigation - Hidden on mobile unless open */}
          <div
            className={`
              ${isMobileNavOpen ? 'translate-x-0' : '-translate-x-full'}
              md:translate-x-0
              absolute md:relative
              z-20
              w-64 h-full
              bg-white
              border-r border-gray-200
              p-4
              overflow-y-auto
              transition-transform duration-200 ease-in-out
            `}
          >
            {navigationContent}
          </div>

          {/* Main Content Area */}
          <div className="flex-1 p-6 overflow-y-auto">
            {activeSection === 'account-profile' && (
              <AccountProfile user={user} onUserUpdate={onUserUpdate} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
